import { useGetJobApplications } from '@modules/jobApplications/hooks/useGetJobApplications';
import { useGetJobApplicationsKanban } from '@modules/jobApplications/hooks/useGetJobApplicationsKanban';
import { defaultJobApplicationFilters } from '@modules/jobApplications/JobApplicationFilter';
import JobApplicationListContainer from '@modules/jobApplications/JobApplicationListContainer';
import { JobApplicationStatus } from '@modules/jobApplications/statuses';
import { Stack } from '@mui/system';
import { usePageSize } from '@hooks/usePageSize';
import { useCallback, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useDebounce } from '@hooks/useDebounce';

interface OutletContextType {
    searchParams: any;
    setSearchParams: (params: any) => void;
}

const BrandJobApplications = () => {
    const { id } = useParams<{ id: string }>();
    useOutletContext<OutletContextType>();
    const { pageSize, storePageSize } = usePageSize({ storageKey: 'brand-job-applications-page-size' });

    const [searchParams, setSearchParams] = useState({
        query: '',
        pagination: {
            page: 1,
            limit: pageSize,
        },
        sortBy: { field: 'createdDate', order: -1 },
        filter: {
            ...defaultJobApplicationFilters,
            brandIds: id ? [id] : [],
        },
    });

    const debouncedSearchParams = useDebounce(searchParams, 500);

    const { jobApplications, loading, isLoadingMore, error, totalDocs, totalPages, loadMore: loadMoreList, forceRefresh } = useGetJobApplications(
        debouncedSearchParams.query,
        debouncedSearchParams.filter,
        debouncedSearchParams.sortBy,
        debouncedSearchParams.pagination.page - 1,
        debouncedSearchParams.pagination.limit,
    );

    const { kanbanData, loadMore } = useGetJobApplicationsKanban(
        debouncedSearchParams.query,
        debouncedSearchParams.filter,
        debouncedSearchParams.sortBy,
        debouncedSearchParams.pagination.limit,
    );

    const handleStatusUpdate = useCallback((_applicationId: number, _newStatus: JobApplicationStatus) => {}, []);

    const onQueryChange = useCallback((value: string) => {
        setSearchParams(prev => ({ ...prev, query: value }));
    }, []);

    if (error) {
        return <div>Erreur lors du chargement des candidatures.</div>;
    }

    return (
        <Stack sx={{ pt: 0, flex: 1, minHeight: '100%', gap: 3, width: '100%' }}>
            <JobApplicationListContainer
                data={jobApplications}
                kanbanData={kanbanData}
                loading={loading}
                isLoadingMore={isLoadingMore}
                totalDocs={totalDocs}
                totalPages={totalPages}
                currentPage={searchParams.pagination.page}
                itemsPerPage={searchParams.pagination.limit}
                onPageChange={page => {
                    setSearchParams(prev => ({
                        ...prev,
                        pagination: { ...prev.pagination, page },
                    }));
                }}
                onItemsPerPageChange={limit => {
                    storePageSize(limit);
                    setSearchParams(prev => ({
                        ...prev,
                        pagination: { ...prev.pagination, page: 1, limit },
                    }));
                }}
                onSortChange={sortBy => {
                    setSearchParams(prev => ({
                        ...prev,
                        sortBy: { field: sortBy.field, order: -1 },
                    }));
                }}
                onQueryChange={onQueryChange}
                onFilterChange={filter => {
                    setSearchParams(prev => ({
                        ...prev,
                        filter: { ...filter, brandIds: id ? [id] : [] },
                    }));
                }}
                filter={searchParams.filter}
                showFilter={true}
                showPagination={true}
                displayMode="job-focus"
                onLoadMoreKanban={loadMore}
                onStatusUpdate={handleStatusUpdate}
                hideChipKeys={['brandIds']}
                onRefresh={forceRefresh}
                onLoadMore={loadMoreList}
                meta={{ totalDocs, page: searchParams.pagination.page, limit: searchParams.pagination.limit }}
            />
        </Stack>
    );
};

export default BrandJobApplications;
